import './ClassList.less';

import { DeleteFilled } from '@ant-design/icons';
import {
  Button,
  Descriptions,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Typography,
} from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  useCarrierUpdate,
  useClassCreate,
  useClassRemove,
  useClassUpdate,
  useClassVehicleMapUpdate,
} from '../../hooks/apiHooks';
import { getAlphabet } from '../../library/util';
import { UserRole } from '../../models/User';
import Container from '../common/Container';
import EditButton from '../common/EditButton';
import ListHeader from '../common/ListHeader';
import PermissionsToggle from '../common/PermissionsToggle';
import ClassModal from '../modals/ClassModal';

/*
interface IProps {
  id: string;
  carrier: Carrier;
  userRole?: UserRole;
  vehicles: Vehicle[];
  classVehicleMap: ClassVehicles;
}
*/

const ClassList = ({ carrier, classVehicleMap, id, userRole, vehicles }) => {
  const createMutation = useClassCreate();
  const updateMutation = useClassUpdate();
  const removeMutation = useClassRemove();
  const carrierUpdateMutation = useCarrierUpdate();
  const updateCarrierVehicleMapMutation = useClassVehicleMapUpdate();

  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [deletableClass, setDeletableClass] = useState();
  const [showAddModal, setShowAddModal] = useState(false);
  const [classNames, setClassNames] = useState({});
  const originalFormData = useRef({});
  const originalClassNames = useRef({});

  const classes = useMemo(() => carrier.classes || [], [carrier]);

  const initializeClassNames = useCallback(() => {
    const names = {};
    classes.forEach((c) => {
      names[c.id] = c.name;
    });
    return names;
  }, [classes]);

  // find classes with changed names
  const changedClassNames = useMemo(() => {
    const changes = [];
    classes.forEach((c) => {
      if (classNames[c.id] && classNames[c.id] !== c.name) {
        changes.push({ ...c, name: classNames[c.id] });
      }
    });
    return changes;
  }, [classNames, classes]);

  // checking if any class name is empty
  const hasEmptyClassName = useMemo(() => {
    return classes.some((c) => !classNames[c.id]?.trim());
  }, [classNames, classes]);

  const onAdd = useCallback(
    (classification) => {
      classification.carrierId = carrier.id;
      createMutation.mutate(classification);
    },
    [carrier.id, createMutation]
  );

  const onDelete = useCallback(() => {
    if (deletableClass) {
      removeMutation.mutate(deletableClass);
    }
  }, [deletableClass, removeMutation]);

  const onSubmit = useCallback(
    async (classVehicles) => {
      // prevent save if any class name is empty
      if (hasEmptyClassName) {
        return;
      }

      // first, update any changed class names
      for (const changedClass of changedClassNames) {
        await updateMutation.mutateAsync(changedClass);
      }

      // then update vehicle assignments
      updateCarrierVehicleMapMutation.mutate(classVehicles);
    },
    [updateCarrierVehicleMapMutation, changedClassNames, updateMutation, hasEmptyClassName]
  );

  const handleSave = useCallback(() => {
    if (hasEmptyClassName) {
      return;
    }
    form.submit();
  }, [form, hasEmptyClassName]);

  const handleEdit = useCallback(() => {
    // store orig values before editing
    originalFormData.current = form.getFieldsValue();
    originalClassNames.current = { ...classNames };
    setIsEditing(true);
  }, [form, classNames]);

  const handleCancel = useCallback(() => {
    // revert form values
    form.setFieldsValue(originalFormData.current);
    // revert class names
    setClassNames(originalClassNames.current);
    setDeletableClass(undefined);
    setIsEditing(false);
  }, [form]);

  const handleClassNameChange = useCallback((classId, newName) => {
    setClassNames((prev) => ({
      ...prev,
      [classId]: newName,
    }));
  }, []);

  const onTogglePermission = useCallback(
    (permission) => {
      const newCarrier = {
        ...carrier,
        classPermission: permission,
      };
      carrierUpdateMutation.mutate(newCarrier);
    },
    [carrier, carrierUpdateMutation]
  );

  useEffect(() => {
    form.setFieldsValue(classVehicleMap);
  }, [classVehicleMap, form]);

  // initialize class names when classes change
  useEffect(() => {
    setClassNames(initializeClassNames());
  }, [initializeClassNames]);

  useEffect(() => {
    if (!isEditing) {
      setDeletableClass(undefined);
    }
  }, [isEditing]);

  useEffect(() => {
    if (createMutation.isSuccess) {
      setShowAddModal(false);
    }
  }, [createMutation.isSuccess]);

  // turn off editing mode when vehicle map is successfully saved
  useEffect(() => {
    if (updateCarrierVehicleMapMutation.isSuccess) {
      setIsEditing(false);
    }
  }, [updateCarrierVehicleMapMutation.isSuccess]);

  const isSaving = updateCarrierVehicleMapMutation.isLoading || updateMutation.isLoading;

  return (
    <Container id={id}>
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Descriptions
          colon={false}
          column={2}
          layout="vertical"
          title={
            <ListHeader>
              <Space>
                <span>Rate Class</span>
                {userRole === UserRole.SUPER_ADMIN && (
                  <PermissionsToggle
                    defaultValue={carrier.classPermission}
                    submitting={carrierUpdateMutation.isLoading}
                    onChange={onTogglePermission}
                  />
                )}
              </Space>

              <Space>
                {isEditing ? (
                  <>
                    <EditButton
                      disabled={hasEmptyClassName}
                      loading={isSaving}
                      onClick={handleSave}
                    >
                      Save
                    </EditButton>
                    <Button onClick={handleCancel}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <EditButton onClick={handleEdit}>
                    Edit
                  </EditButton>
                )}
                <Button
                  ghost
                  style={{ width: 120 }}
                  type="primary"
                  onClick={() => setShowAddModal(true)}
                >
                  Add Class
                </Button>
              </Space>
            </ListHeader>
          }
        >
          {classes.map((classification, index) => (
            <Descriptions.Item
              key={classification.id}
              label={
                <Space>
                  {isEditing ? (
                    <Input
                      placeholder={`Class ${getAlphabet(index)}`}
                      size="middle"
                      status={!classNames[classification.id]?.trim() ? 'error' : undefined}
                      style={{ width: 155 }}
                      value={classNames[classification.id] || ''}
                      onChange={(e) => handleClassNameChange(classification.id, e.target.value)}
                    />
                  ) : (
                    <span>
                      {classification.name}{' '}
                      {classification.name !== `Class ${getAlphabet(index)}` && (
                        <Typography.Text type="secondary">
                          (Class {getAlphabet(index)})
                        </Typography.Text>
                      )}
                    </span>
                  )}
                  {isEditing && (
                    <Popconfirm
                      okButtonProps={{ loading: removeMutation.isLoading }}
                      open={deletableClass?.id === classification.id}
                      title="Are you sure to delete this class?"
                      onCancel={() => setDeletableClass(undefined)}
                      onConfirm={onDelete}
                    >
                      <DeleteFilled
                        style={{ color: '#ff4d4f', cursor: 'pointer' }}
                        onClick={() => setDeletableClass(classification)}
                      />
                    </Popconfirm>
                  )}
                </Space>
              }
            >
              <Form.Item
                key={classification.id}
                noStyle
                name={classification.id}
              >
                <Select
                  className="class-list__vehicle-select"
                  disabled={!isEditing}
                  mode="multiple"
                  placeholder="Vehicle Types"
                  style={{ width: '100%' }}
                >
                  {vehicles.map((vehicle) => (
                    <Select.Option key={vehicle.id} value={vehicle.id}>
                      {vehicle.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Descriptions.Item>
          ))}
          {classes.length % 2 === 1 && (
            <Descriptions.Item>&nbsp;</Descriptions.Item>
          )}
        </Descriptions>
      </Form>
      {showAddModal && (
        <ClassModal
          classes={classes}
          error={createMutation.error}
          submitting={createMutation.isLoading}
          onClose={() => setShowAddModal(false)}
          onSubmit={onAdd}
        />
      )}
    </Container>
  );
};

export default ClassList;
